import { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw, Wallet, CreditCard, Globe, Loader2, XCircle, CheckCircle2 } from "lucide-react";
import { adminApi } from "../api";

const EUR = (v) => (Number(v) || 0).toLocaleString("pt-PT", { style: "currency", currency: "EUR" });

const METHOD_META = {
  CP: { label: "Cofidis Pay",    Icon: Wallet,     color: "text-[#E60013]" },
  PP: { label: "PayPal",         Icon: Globe,      color: "text-[#0070BA]" },
  CT: { label: "EasyPay Cartão", Icon: CreditCard, color: "text-[#0F1829]" },
};

function fmtAge(minutes) {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  if (h < 24) return `${h}h ${minutes % 60}min`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

/**
 * StuckPaymentsAlert — widget de alerta no Admin Home.
 *
 * Mostra encomendas em AP cujo URL externo (Cofidis/PayPal/EasyPay) não foi
 * gerado pelo legacy. Permite forçar nova chamada `changePayment` por linha
 * sem ter de navegar para o detalhe da encomenda.
 */
export default function StuckPaymentsAlert() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState({}); // { [orderId]: true }
  const [results, setResults] = useState({}); // { [orderId]: { ok, message, url } }

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await adminApi.stuckPaymentUrls(15);
      setData(r.data || []);
    } catch (e) {
      setErr(e.message || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRegenerate = async (orderId) => {
    setBusy((b) => ({ ...b, [orderId]: "regen" }));
    setResults((r) => ({ ...r, [orderId]: null }));
    try {
      const r = await adminApi.regeneratePaymentUrl(orderId);
      let message = r.legacy_message || (r.ok ? "Link regenerado." : "Falha.");
      let success = r.ok;
      if (r.ok && r.tipo_pagamento === "CP" && !r.new_url) {
        message = "Legacy aceitou mas EuPago/Cofidis recusou silenciosamente. Esta encomenda só pode ser recuperada cancelando — o cliente terá de refazer.";
        success = false;
      }
      setResults((s) => ({
        ...s,
        [orderId]: { ok: success, message, url: r.new_url, kind: "regen" },
      }));
      if (success) setTimeout(load, 1200);
    } catch (e) {
      setResults((s) => ({ ...s, [orderId]: { ok: false, message: e.message, kind: "regen" } }));
    } finally {
      setBusy((b) => ({ ...b, [orderId]: false }));
    }
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm(`Cancelar encomenda #${orderId} e notificar o cliente por email para refazer?`)) return;
    setBusy((b) => ({ ...b, [orderId]: "cancel" }));
    setResults((r) => ({ ...r, [orderId]: null }));
    try {
      const r = await adminApi.cancelStuckOrder(orderId);
      const message = r.ok
        ? `Encomenda cancelada (CC). Email ${r.email_sent ? `enviado para ${r.customer_email}` : "não enviado (falha SMTP)"}.`
        : "Falha ao cancelar.";
      setResults((s) => ({ ...s, [orderId]: { ok: r.ok, message, kind: "cancel" } }));
      if (r.ok) setTimeout(load, 1200);
    } catch (e) {
      setResults((s) => ({ ...s, [orderId]: { ok: false, message: e.message, kind: "cancel" } }));
    } finally {
      setBusy((b) => ({ ...b, [orderId]: false }));
    }
  };

  const handleIgnore = async (orderId) => {
    const reason = window.prompt(
      `Dispensar o alerta da encomenda #${orderId} (não a cancela; a encomenda mantém o estado atual).\n\nMotivo:`,
      "Paga por outro meio de pagamento",
    );
    if (reason === null) return; // cancelado pelo utilizador
    setBusy((b) => ({ ...b, [orderId]: "ignore" }));
    setResults((r) => ({ ...r, [orderId]: null }));
    try {
      const r = await adminApi.ignoreStuckOrder(orderId, (reason || "").trim() || "Paga por outro meio de pagamento");
      setResults((s) => ({ ...s, [orderId]: { ok: r.ok, message: "Alerta dispensado.", kind: "ignore" } }));
      if (r.ok) setTimeout(load, 1000);
    } catch (e) {
      setResults((s) => ({ ...s, [orderId]: { ok: false, message: e.message, kind: "ignore" } }));
    } finally {
      setBusy((b) => ({ ...b, [orderId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="p-4 bg-white border border-[#E6E1D6] rounded-md text-sm text-[#6B7280]" data-testid="stuck-payments-loading">
        <Loader2 size={14} className="inline mr-2 animate-spin" /> A verificar URLs de pagamento…
      </div>
    );
  }

  if (err) {
    return (
      <div className="p-4 bg-[#FEF2F2] border border-[#FECACA] rounded-md text-sm text-[#991B1B]" data-testid="stuck-payments-error">
        Erro: {err}
      </div>
    );
  }

  if (!data.length) {
    return null; // Hide widget completely when nothing is stuck — keeps dashboard clean.
  }

  return (
    <section className="mb-8" data-testid="stuck-payments-alert">
      <div className="p-5 bg-[#FFF7ED] border border-[#FDBA74] rounded-md">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-[#C2410C]" />
            <h2 className="text-base font-bold uppercase tracking-[0.12em] text-[#7C2D12]">
              URLs de pagamento em falta · {data.length}
            </h2>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="stuck-payments-refresh"
            className="text-xs text-[#7C2D12] hover:text-[#0F1829] flex items-center gap-1"
          >
            <RefreshCw size={12} /> Actualizar
          </button>
        </div>
        <p className="text-xs text-[#7C2D12] mb-4">
          Encomendas em AP há mais de 15 minutos com Cofidis/PayPal/EasyPay onde o legacy não populou o URL externo. Clique em <strong>Regerar link</strong> para forçar nova chamada à gateway.
        </p>

        <ul className="divide-y divide-[#FED7AA]">
          {data.map((o) => {
            const meta = METHOD_META[o.ID_TipoPagamento] || { label: o.ID_TipoPagamento, Icon: Wallet, color: "text-[#0F1829]" };
            const result = results[o.ID_Encomenda];
            const isBusy = busy[o.ID_Encomenda];
            return (
              <li key={o.ID_Encomenda} className="py-3 flex items-center gap-3" data-testid={`stuck-row-${o.ID_Encomenda}`}>
                <meta.Icon size={18} className={`shrink-0 ${meta.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-mono text-[#0F1829]">
                    #{o.ID_Encomenda}
                    <span className="ml-2 text-[#4B5563] font-sans">{meta.label}</span>
                    <span className="ml-2 text-[#4B5563] font-sans">· {EUR(o.PrecoFinal)}</span>
                    <span className="ml-2 text-[#978D78] font-sans text-xs">há {fmtAge(o.minutes_in_ap)}</span>
                  </div>
                  <div className="text-xs text-[#6B7280] truncate">
                    {o.NomeCompleto || "Cliente"} · {o.Email || "—"}
                  </div>
                  {result && (
                    <div className={`text-xs mt-1 ${result.ok ? "text-[#065F46]" : "text-[#991B1B]"}`} data-testid={`stuck-result-${o.ID_Encomenda}`}>
                      {result.ok ? "✓" : "✗"} {result.message}
                      {result.url && <span className="ml-1">— <a href={result.url} target="_blank" rel="noreferrer" className="underline">abrir link</a></span>}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  disabled={!!isBusy}
                  onClick={() => handleRegenerate(o.ID_Encomenda)}
                  data-testid={`stuck-regenerate-${o.ID_Encomenda}`}
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[#FDBA74] bg-white hover:bg-[#0F1829] hover:text-white hover:border-[#0F1829] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isBusy === "regen" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  Regerar link
                </button>
                <button
                  type="button"
                  disabled={!!isBusy}
                  onClick={() => handleCancel(o.ID_Encomenda)}
                  data-testid={`stuck-cancel-${o.ID_Encomenda}`}
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[#FCA5A5] bg-white text-[#991B1B] hover:bg-[#991B1B] hover:text-white hover:border-[#991B1B] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isBusy === "cancel" ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={!!isBusy}
                  onClick={() => handleIgnore(o.ID_Encomenda)}
                  data-testid={`stuck-ignore-${o.ID_Encomenda}`}
                  title="Dispensar o alerta sem cancelar a encomenda (ex.: paga por outro meio)"
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[#86EFAC] bg-white text-[#065F46] hover:bg-[#065F46] hover:text-white hover:border-[#065F46] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isBusy === "ignore" ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                  Dispensar
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
